import { useState } from 'react';
import { useEffect } from 'react';

import cn from 'classnames';
import { FilesFolders } from 'constants/files-folders-enum';
import FeatherIcon from 'feather-icons-react';
import { uploadSigned } from 'helpers/aws-s3';
import PropTypes from 'prop-types';
import { useDropzone } from 'react-dropzone';
import * as mime from 'react-native-mime-types';

import { ImageCropModal } from 'components/ImageCropModal/ImageCropModal';
import { Loader } from 'components/Loader/Loader';

import {
  useCreateUploadMutation,
  useDeleteUploadMutation,
  useLazyGetUploadQuery,
  useLazySignUploadQuery,
} from 'api/upload-api';

import styles from './ImagePicker.module.scss';

export const ImagePicker = ({
  value,
  disabled,
  error,
  modelParams = {},
  metadata = {},
  bucketFolder = FilesFolders.Assets,
  onChange = () => {},
  previewAsCover = false,
  cropAspect = null,
  cropRecommendedSize = null,
}) => {
  const [imageUrl, setImageUrl] = useState();
  const [cropSrc, setCropSrc] = useState(null);
  const [pendingFile, setPendingFile] = useState(null);

  const [getUpload] = useLazyGetUploadQuery();
  const [signUpload] = useLazySignUploadQuery();
  const [createUpload] = useCreateUploadMutation();
  const [deleteUpload] = useDeleteUploadMutation();

  const [fetching, setFetching] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);

  useEffect(() => {
    if (value) {
      setFetching(true);
      getUpload(value)
        .unwrap()
        .then(result => {
          setImageUrl(result.url);
          setFetching(false);
        });
    } else {
      setImageUrl(null);
    }
  }, [value]);

  const uploadFile = async file => {
    setUploading(true);
    setImageLoading(true);

    const params = {
      file_name: file.name,
      file_type: file.type,
      file_ext:
        mime.extension(file.type) ||
        (file.name && file.name.includes('.') ? file.name.split('.').pop() : null) ||
        'bin',
      bucket_folder: bucketFolder,
    };

    const signedFile = await signUpload(params).unwrap();
    await uploadSigned(signedFile.url, file, file.type);

    const uploadData = {
      ...modelParams,
      metadata,
      file_name: signedFile.file_name,
      file_type: signedFile.file_type,
      hash_key: signedFile.key,
    };

    const { data: upload } = await createUpload(uploadData);

    setImageUrl(upload.url);
    onChange(upload.id);
    setUploading(false);
  };

  const onDrop = async acceptedFiles => {
    const file = acceptedFiles[0];
    if (!file) return;

    if (cropAspect) {
      const objectUrl = URL.createObjectURL(file);
      setPendingFile(file);
      setCropSrc(objectUrl);
      return;
    }

    await uploadFile(file);
  };

  const handleCropConfirm = async blob => {
    URL.revokeObjectURL(cropSrc);
    setCropSrc(null);
    const croppedFile = new File([blob], pendingFile.name, { type: 'image/png' });
    setPendingFile(null);
    await uploadFile(croppedFile);
  };

  const handleCropCancel = () => {
    URL.revokeObjectURL(cropSrc);
    setCropSrc(null);
    setPendingFile(null);
  };

  const onRemove = async () => {
    if (value) {
      setUploading(true);
      await deleteUpload(value);
      setUploading(false);
    }
    setImageUrl(null);
    onChange(null);
  };

  const { getRootProps, getInputProps } = useDropzone({ onDrop, accept: { 'image/*': [] } });

  const errorBorder = error ? { border: '1px solid red' } : null;
  return (
    <>
      {cropSrc && (
        <ImageCropModal
          imageSrc={cropSrc}
          aspect={cropAspect}
          recommendedSize={cropRecommendedSize}
          onConfirm={handleCropConfirm}
          onCancel={handleCropCancel}
        />
      )}
      <div
        className={cn(styles.root, 'margin-top', previewAsCover && styles.rootCoverPreview)}
        style={errorBorder}>
        {uploading || (fetching && <Loader />)}

        {imageUrl && !uploading && !fetching && (
          <div className={cn(previewAsCover ? styles.coverPreviewHost : undefined)}>
            {imageLoading && <Loader />}
            {previewAsCover ? (
              <>
                <div
                  className={styles.backgroundCoverPreview}
                  style={{ backgroundImage: `url(${imageUrl})` }}
                  role="img"
                  aria-label="Selected image preview"
                />
                <img
                  src={imageUrl}
                  alt=""
                  className={styles.hiddenMeasureImg}
                  onLoad={() => setImageLoading(false)}
                />
              </>
            ) : (
              <img
                src={imageUrl}
                className={styles.imagePreview}
                onLoad={() => setImageLoading(false)}
              />
            )}
            {!imageLoading && !disabled && (
              <div className={styles.removeImageIconWrapper} onClick={onRemove}>
                <FeatherIcon className={styles.removeImageIcon} icon="trash-2" size={40} />
              </div>
            )}
          </div>
        )}

        {!imageUrl && !uploading && !fetching && (
          <div {...getRootProps({ className: styles.dropzone })}>
            <input {...getInputProps()} />
            <FeatherIcon icon="image" size={30} className={styles.icon} />
            <p>
              Drag or <span className="link">browse</span> to upload
            </p>
          </div>
        )}
      </div>
      {error && <span className={styles.error}>{error}</span>}
    </>
  );
};

ImagePicker.propTypes = {
  onChange: PropTypes.func,
  value: PropTypes.any,
  previewAsCover: PropTypes.bool,
  cropAspect: PropTypes.number,
  cropRecommendedSize: PropTypes.string,
};
